import type { FilterConfig, TraffickingFiltersInput } from '../../../../apis/graphql';
import { FilterInputType, FilterOperator, StringOperator } from '../../../../apis/graphql';
import type { GlobalFilters } from '../../../../contexts/FilterContext/hooks';
import {
  getLineItemStatusFiltersInput,
  isOperatorFilterValue,
  wrapAsStringOperatorFilter,
} from '../../../../contexts/FilterContext/hooks/utils';
import { filterOptionsToValues } from '../multiSelectFilterUtils';

export const DEFAULT_NEW_FILTER_NAME = 'Custom Filter 1';

// KATANA-1509: same FilterOperator -> StringOperator narrowing as getConfigDrivenFilterVars in
// pages/Trafficking/utils.ts — Brand's operator is only ever Contains/DoesNotContain, so this is a
// safe narrowing map between the UI enum and the wire enum, not a lossy one.
const toStringOperator = (operator: FilterOperator): StringOperator =>
  operator === FilterOperator.DoesNotContain ? StringOperator.DoesNotContain : StringOperator.Contains;

export const buildCustomFilterPayload = (
  filters: GlobalFilters,
  filterConfigs: FilterConfig[]
): TraffickingFiltersInput => {
  const payload: Record<string, unknown> = {};

  for (const config of filterConfigs) {
    const value = filters[config.id as keyof GlobalFilters];
    if (value != null) {
      // KATANA-1509: Brand's committed GlobalFilters value is the canonical `{ operator, values }`
      // object, not a flat array — flatten values to bare ids (dropping labels, same as every
      // other saved field) while forwarding the operator, narrowed from the UI's FilterOperator to
      // the wire StringOperator via toStringOperator. Checked before the generic StringOperator
      // branch below regardless of Brand's config.inputType (which is STRING_OPERATOR too as of the
      // KATANA-1509 follow-up, matching agency/industry): that branch's wrapAsStringOperatorFilter
      // only wraps a *flat* array (always as Contains) and passes an already object-shaped value
      // through unchanged, so it wouldn't flatten Brand's DropdownOptionFilter[] labels to bare ids
      // or narrow its genuine Contains/DoesNotContain toggle — this explicit branch has to run first.
      if (isOperatorFilterValue(value)) {
        payload[config.id] = {
          operator: toStringOperator(value.operator),
          values: filterOptionsToValues(value.values),
        };
        continue;
      }
      // KATANA-1499 follow-up: config-driven directly off the entry's own inputType — no Set
      // needed since the FilterConfig object is already in hand here. Wraps every
      // always-CONTAINS string/enum save field into {operator, values} shape, flattening any
      // DropdownOptionFilter values to bare ids (same as the shareable-link save path) so the
      // custom filter payload never stores display labels. No-op for every other config id.
      payload[config.id] =
        config.inputType === FilterInputType.StringOperator ? wrapAsStringOperatorFilter(value) : value;
    }

    if (config.includeExclude?.excludeKey) {
      const excludeValue = filters[config.includeExclude.excludeKey as keyof GlobalFilters];
      if (excludeValue != null) {
        payload[config.includeExclude.excludeKey] = excludeValue;
      }
    }
  }

  if (payload.lineItemStatusList) {
    const { lineItemStatusList, lineItemSubStatusList } = getLineItemStatusFiltersInput(
      payload.lineItemStatusList as string[]
    );
    if (lineItemStatusList) payload.lineItemStatusList = lineItemStatusList;
    if (lineItemSubStatusList) payload.lineItemSubStatusList = lineItemSubStatusList;
  }

  return payload as TraffickingFiltersInput;
};
